import { useState } from 'react';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Input } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { useBorrarCategoria, useCategorias, useCrearCategoria, type CategoriaDeCurso } from './api';

/**
 * Categorias.tsx — crear y borrar las categorías del catálogo.
 *
 * ## Lo que ya no hace falta cuidar a mano
 *
 * El vanilla tenía una función `_refreshCategories()` cuyo único trabajo era
 * repintar **dos sitios a la vez** —la lista del panel y las píldoras del
 * catálogo—, porque al crear o borrar una se quedaban desincronizados. Aquí los
 * dos leen la misma consulta: invalidarla los actualiza a los dos, y no hay
 * forma de olvidarse de uno.
 *
 * ## Y el `confirm()` que se pisaba a sí mismo
 *
 * Borrar preguntaba con `Utils.confirm`, que reutiliza el mismo
 * `#modal-overlay` que el panel de categorías, así que la pregunta tapaba el
 * panel del que salía. Aquí el diálogo es propio y vive por encima.
 */
export function Categorias({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const { data, isLoading, isError, error, refetch } = useCategorias(abierto);
  const crear = useCrearCategoria();
  const borrar = useBorrarCategoria();

  const [nombre, setNombre] = useState('');
  const [color, setColor] = useState('#0D2B4E');
  const [borrando, setBorrando] = useState<CategoriaDeCurso | null>(null);

  const categorias = data ?? [];

  function crearCategoria() {
    const limpio = nombre.trim();
    if (!limpio) {
      Utils.toast('Escribe un nombre para la categoría', 'warning');
      return;
    }
    crear.mutate(
      { name: limpio, color },
      {
        onSuccess: () => {
          setNombre('');
          Utils.toast('Categoría creada', 'success');
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo crear', 'error'),
      },
    );
  }

  return (
    <Drawer
      abierto={abierto}
      onCambio={(v) => { if (!v) { setBorrando(null); onCerrar(); } }}
      titulo="Categorías de cursos"
      descripcion="Agrupan los cursos en el filtro del catálogo."
      pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
    >
      <div className="flex flex-col gap-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => { e.preventDefault(); crearCategoria(); }}
        >
          <Field label="Nombre" className="min-w-40 flex-1">
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Liderazgo"
            />
          </Field>
          <Field label="Color">
            <input
              type="color"
              aria-label="Color de la categoría"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-11 w-14 cursor-pointer rounded-sm border border-field-border bg-surface p-1"
            />
          </Field>
          <Button type="submit" loading={crear.isPending}>
            <i className="bi bi-plus-lg" aria-hidden="true" /> Crear
          </Button>
        </form>

        {isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : isError ? (
          <EstadoError err={error} que="las categorías" onReintentar={() => void refetch()} />
        ) : !categorias.length ? (
          <p className="text-3 text-muted">
            Sin categorías todavía. Crea una arriba para organizar el catálogo.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {categorias.map((cat) => (
              <li
                key={cat.id}
                className="flex items-center gap-2.5 rounded-sm border border-line p-2.5"
              >
                <span
                  className="size-3.5 shrink-0 rounded-xs"
                  style={{ background: cat.color || '#0D2B4E' }}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1 truncate text-3 font-semibold text-ink">
                  {cat.name}
                </span>
                <span className="shrink-0 text-2 text-muted">
                  {cat.course_count || 0} {(cat.course_count || 0) === 1 ? 'curso' : 'cursos'}
                </span>
                {/* El servidor no borra una categoría con cursos: pide
                    reasignarlos antes. El diálogo decía lo contrario —«se quedan
                    sin categoría, no se borran»— y al confirmar llegaba un 400.
                    Se dice aquí, antes de pulsar. */}
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Eliminar la categoría ${cat.name}`}
                  disabled={(cat.course_count || 0) > 0}
                  razonDeshabilitado={`Tiene ${cat.course_count} ${cat.course_count === 1 ? 'curso' : 'cursos'}: reasígnalos a otra categoría antes de eliminarla.`}
                  onClick={() => setBorrando(cat)}
                >
                  <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar categoría"
        descripcion={
          borrando
            ? `«${borrando.name}» desaparecerá del filtro del catálogo. No tiene cursos, así que ninguno cambia.`
            : ''
        }
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!borrando) return;
          borrar.mutate(borrando.id, {
            onSuccess: () => { setBorrando(null); Utils.toast('Categoría eliminada', 'info'); },
            onError: (err) => {
              setBorrando(null);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
            },
          });
        }}
      />
    </Drawer>
  );
}
